import React from "react";
import ReactModal from "react-modal";
import { motion, AnimatePresence } from "framer-motion";
import { FiAward, FiMapPin, FiCalendar, FiHash, FiX, FiSave, FiPlus, FiEdit2 } from "react-icons/fi";
import { axiospost, axiosput } from "../../helpers/httprequest";
import swal from "../../helpers/swal";
import { Form, Formik } from "formik";
import { createOLevelSitting } from "../../utils/validations";
import { useQueryClient } from "@tanstack/react-query";
import { Card } from "../../components/ui/Card";
import { Alert, AlertTitle, AlertDescription } from "../../components/ui/Alert";
import FormInput from "../../components/ui/FormInput";
import FormSelect from "../../components/ui/FormSelect";
import { Button } from "../../components/ui/Button";
import { cn } from "../../lib/utils";

const BtnOLevel = ({ modules }) => {
  const [showmodal, setshowmodal] = React.useState(false);
  const queryClient = useQueryClient();

  const _save = async (values) => {
    try {
      let req;
      if (modules === undefined) {
        req = await axiospost("ms_applicant_sitting", values);
      } else {
        req = await axiosput(`ms_applicant_sitting/${modules?._id}`, values);
      }
      if (req.status !== 200) {
        throw new Error("Sorry, system could not finish processing your request");
      }
      const { data: result } = req;
      if (result && result.type === "success") {
        const { message, type } = result;
        swal(type, message);
        queryClient.invalidateQueries({ queryKey: ["oLevelSitting"] });
        queryClient.invalidateQueries({ queryKey: ["dashboardDataset"] });
        setshowmodal(false);
      } else {
        throw new Error(result.message);
      }
    } catch ({ message }) {
      swal("warning", message);
    }
  };

  const _show = () => {
    setshowmodal(true);
  };

  const examinationOptions = [
    { value: "WAEC", label: "WAEC - West African Examinations Council" },
    { value: "NECO", label: "NECO - National Examinations Council" },
    { value: "NABTEB", label: "NABTEB - National Business and Technical Examinations Board" },
    { value: "NBAIS", label: "NBAIS - National Board for Arabic and Islamic Studies" },
    { value: "GCE", label: "GCE - General Certificate of Education" },
    { value: "GRADE II", label: "GRADE II - Teacher Certificate" },
  ];

  return (
    <div>
      <ReactModal
        isOpen={showmodal}
        onRequestClose={() => setshowmodal(false)}
        className="flex items-center justify-center min-h-screen p-4"
        overlayClassName="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
        ariaHideApp={false}
      >
        <AnimatePresence>
          {showmodal && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className="w-full min-w-[60%] max-w-4xl mx-auto"
            >
              <Card padding="none" className="shadow-2xl">
                {/* Gradient Header */}
                <div className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 p-6 rounded-t-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="bg-white/20 backdrop-blur-sm p-3 rounded-lg">
                        <FiAward className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl md:text-2xl font-bold text-white">
                          {modules ? "Edit O-Level Sitting" : "Add O-Level Sitting"}
                        </h2>
                        <p className="text-primary-100 text-sm mt-1">
                          Provide your examination details
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setshowmodal(false)}
                      className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
                      aria-label="Close modal"
                    >
                      <FiX className="h-5 w-5 text-white" />
                    </button>
                  </div>
                </div>

                {/* Modal Body */}
                <div className="p-6 space-y-4">
                  {/* Info Alert */}
                  <Alert variant="info">
                    <AlertTitle>What is an O-Level Sitting?</AlertTitle>
                    <AlertDescription>
                      An O-Level sitting refers to a single examination session where you took your
                      O-Level exams (WAEC, NECO, NABTEB, etc.). Most applicants have one or two sittings.
                      Please provide accurate examination details including type, center number, year, and
                      examination number.
                    </AlertDescription>
                  </Alert>

                  <Formik
                    initialValues={{
                      examinationtype: modules ? modules?.examinationtype : "",
                      examinationcenter: modules ? modules?.examinationcenter : "",
                      examinationyear: modules ? modules?.examinationyear : "",
                      examinationnumber: modules ? modules?.examinationnumber : "",
                    }}
                    validationSchema={createOLevelSitting}
                    onSubmit={_save}
                  >
                    {({ isSubmitting }) => (
                      <Form className="space-y-4">
                        {/* Form Fields Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Examination Type */}
                          <div className="md:col-span-2">
                            <FormSelect
                              name="examinationtype"
                              label="Examination Type"
                              options={examinationOptions}
                              required
                              helperText="Select the examination body you wrote"
                              searchable
                              clearable
                            />
                          </div>

                          {/* Examination Center */}
                          <FormInput
                            name="examinationcenter"
                            label="Examination Center Number"
                            type="text"
                            leftIcon={<FiMapPin />}
                            required
                            helperText="e.g., 0123456"
                            maxLength={20}
                          />

                          {/* Examination Year */}
                          <FormInput
                            name="examinationyear"
                            label="Examination Year"
                            type="text"
                            leftIcon={<FiCalendar />}
                            required
                            helperText="e.g., 2020"
                            maxLength={4}
                          />

                          {/* Examination Number */}
                          <div className="md:col-span-2">
                            <FormInput
                              name="examinationnumber"
                              label="Examination Number"
                              type="text"
                              leftIcon={<FiHash />}
                              required
                              helperText="Enter your complete examination number"
                              maxLength={50}
                            />
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-neutral-200">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setshowmodal(false)}
                            className="sm:flex-1"
                            leftIcon={<FiX />}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            variant="primary"
                            loading={isSubmitting}
                            className="sm:flex-1"
                            leftIcon={<FiSave />}
                          >
                            {isSubmitting ? "Saving..." : modules ? "Update Sitting" : "Save Sitting"}
                          </Button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </ReactModal>

      {/* Add Button */}
      {!modules && (
        <Button
          type="button"
          variant="primary"
          size="md"
          onClick={_show}
          leftIcon={<FiPlus className="h-4 w-4" />}
        >
          Add Sitting
        </Button>
      )}

      <div>
        {modules && (
          <Button
            onClick={_show}
            variant="primary"
            size="icon"
            className="h-9 w-9 rounded-full"
            aria-label="Edit O-Level sitting"
          >
            <FiEdit2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default BtnOLevel;
